import FavoriteIcon from '@components/icons/FavoriteIcon';
import useBaseDataContext from '@context/useBaseDataContext';
import useMyRouting from '@context/useMyRouting/useMyRouting';
import openAsyncUrlInNewTab from '@helpers/openAsyncUrlInNewTab';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import restoreServiceData from '@pages/AdditionalServices/containers/methods/basket/restoreServiceData';
import { SHARE_SHORT_LINK_KEY } from '@pages/AdditionalServices/tabs/DeliveryForm/consts';
import RouteSummary from '@pages/Basket/components/ServicesList/components/OrderList/components/OrderListItem/components/RouteSummary';
import ServiceBasketItem from '@pages/Basket/components/ServicesList/components/OrderList/components/OrderListItem/components/ServiceBasketItem';
import ServiceGroupBasketItem from '@pages/Basket/components/ServicesList/components/OrderList/components/OrderListItem/components/ServiceGroupBasketItem';
import { getCurrencyFromProduct } from '@pages/Basket/components/ServicesList/helpers';
import { getGeneratedShortLink } from '@pages/Favorites/helpers';
import { OrderProduct } from '@services/requests/orderService/orderProductsLoader/interface';
import { ProductSummaryProps } from '@services/requests/orderService/types';
import React, { ComponentType } from 'react';
import { useTranslation } from 'react-i18next';

import { FavoritesMenuItemStyled } from './StyledComponents';

/**
 * Пропсы компонента
 */
type FavoritesMenuItemProps = {
  product: OrderProduct;
  onDelete: () => void;
};

/**
 * Компонент карточки по типу продукта, как на странице избранного
 */
const getSummaryComponent = (
  productType: OrderProduct['product_type']
): ComponentType<ProductSummaryProps> => {
  switch (productType) {
    case 'route':
      return RouteSummary;
    case 'complex':
      return ServiceGroupBasketItem;
    case 'service':
      return ServiceBasketItem;
    default:
      return () => null;
  }
};

/**
 * Элемент краткого меню избранного
 */
export const FavoritesMenuItem = ({ product, onDelete }: FavoritesMenuItemProps) => {
  const { t } = useTranslation(['Actions', 'Common']);
  const [settings] = useMyRouting().useSettings().useState();
  const { useBaseData } = useBaseDataContext();
  const [baseData] = useBaseData().useState();

  const SummaryComponent = getSummaryComponent(product.product_type);
  const currencyCode = getCurrencyFromProduct([product], baseData?.currencies ?? [])?.code ?? '';

  const fullPrice = Math.round(product.price * product.amount * 100) / 100;
  const isShowZeroInsteadText =
    ['service', 'complex'].includes(product.product_type) &&
    !!restoreServiceData(product)?.setUpService?.ServiceData.settings.IsNeedShowZeroPriceInCart;
  const priceText =
    fullPrice > 0 || isShowZeroInsteadText
      ? `${fullPrice.toLocaleString()} ${currencyCode}`
      : t('Common:priceByRequest');

  const openCalculationHandler = () =>
    openAsyncUrlInNewTab(async () => {
      const shortLink = await getGeneratedShortLink(product);

      return shortLink ? `/?${SHARE_SHORT_LINK_KEY}=` + shortLink : undefined;
    });

  return (
    <FavoritesMenuItemStyled className="calculations-menu__item">
      <div className="calculations-menu__item-content">
        <SummaryComponent
          product={product}
          langId={settings.primaryLanguageId}
          orderCurrency={settings.currencyId}
          currencyCode={currencyCode}
          products={[]}
        />
      </div>
      <Typography variant={'body1'} className="price">
        {priceText}
      </Typography>
      <div className="calculations-menu__actions">
        <Tooltip title={t('Actions:RefreshCalculation')}>
          <IconButton
            size={'small'}
            color={'inherit'}
            className="calculations-menu__accent-button"
            onClick={openCalculationHandler}
          >
            <OpenInNewIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title={t('Actions:FromFavorites')}>
          <IconButton
            size={'small'}
            color={'inherit'}
            className="calculations-menu__accent-button"
            onClick={onDelete}
          >
            <FavoriteIcon
              className="calculations-menu__favorite-icon"
              fill={'#3460B4'}
              stroke={'#3460B4'}
            />
          </IconButton>
        </Tooltip>
      </div>
    </FavoritesMenuItemStyled>
  );
};
